import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { ErrorState, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { EVENT_KIND_LABEL, PARTICIPATION_LABEL, PAYMENT_LABEL, formatEventWhen, isPastEvent } from '../data/events'
import { useAthleteEvents, useInvalidateEvent, useParticipantMarks, useSingleFlight } from '../data/hooks'
import { isoDate } from '../data/schedule'
import type { AthleteEventItem } from '../data/types'
import { AddToEventSheet, ParticipantSheet } from './EventSheets'

/** Сколько прошедших событий видно сразу: остальные - по «Показать все». */
const PAST_VISIBLE = 3

/**
 * Блок «Соревнования и сборы» в карточке спортсмена (events.md): куда записан
 * и с каким статусом; касание строки - статус, взнос, результат, «Убрать»;
 * «Добавить в событие» - шторка со списком событий.
 */
export function AthleteEvents({ athleteId, athleteName }: { athleteId: string; athleteName: string }) {
  const { now } = useEnv()
  const list = useAthleteEvents(athleteId)
  const [adding, setAdding] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [showAllPast, setShowAllPast] = useState(false)
  const today = isoDate(now())

  const items = list.data ?? []
  const upcoming = items
    .filter((i) => !isPastEvent(i.event, today))
    .sort((a, b) => a.event.start.date.localeCompare(b.event.start.date))
  const past = items
    .filter((i) => isPastEvent(i.event, today))
    .sort((a, b) => b.event.start.date.localeCompare(a.event.start.date))
  const visiblePast = showAllPast ? past : past.slice(0, PAST_VISIBLE)
  const current = openId ? items.find((i) => i.event.id === openId) : undefined

  const row = (i: AthleteEventItem) => (
    <li key={i.event.id}>
      <button type="button" className="pc-list-row" onClick={() => setOpenId(i.event.id)}>
        <span className="pc-list-row__main">
          <span className="pc-list-row__title" style={{ display: 'block' }}>
            {i.event.name}
          </span>
          <span className="pc-list-row__meta">
            {EVENT_KIND_LABEL[i.event.kind]} / {formatEventWhen(i.event, now().getFullYear(), false)}
          </span>
        </span>
        <span className="pc-list-row__aside">
          {[PARTICIPATION_LABEL[i.status], i.result].filter(Boolean).join(' / ')}
          {i.event.fee && (
            <span style={{ display: 'block' }}>{i.payment === 'not_paid' ? 'Взнос: нет отметки' : PAYMENT_LABEL[i.payment]}</span>
          )}
        </span>
        <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
      </button>
    </li>
  )

  return (
    <section className="pc-section" aria-labelledby="pc-athlete-events">
      <h2 id="pc-athlete-events" className="pc-section-title">
        Соревнования и сборы
      </h2>
      {list.isPending ? (
        <Skeleton rows={1} label="Загружаю события спортсмена" />
      ) : list.isError ? (
        <ErrorState title="Не удалось загрузить события" onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
          Пока не записан ни на одно событие.
        </p>
      ) : (
        <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
          {upcoming.map(row)}
          {visiblePast.map(row)}
        </ul>
      )}
      {past.length > PAST_VISIBLE && !showAllPast && (
        <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 8 }} onClick={() => setShowAllPast(true)}>
          Показать все прошедшие ({past.length})
        </button>
      )}
      <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 8 }} onClick={() => setAdding(true)}>
        Добавить в событие
      </button>

      {adding && (
        <AddToEventSheet
          athleteId={athleteId}
          athleteName={athleteName}
          joined={new Set(items.map((i) => i.event.id))}
          onClose={() => setAdding(false)}
        />
      )}
      {current && <AthleteParticipation item={current} athleteId={athleteId} athleteName={athleteName} onClose={() => setOpenId(null)} />}
    </section>
  )
}

/** Участие спортсмена в одном событии: та же шторка, что в карточке события, сохранение видно в ней. */
function AthleteParticipation({
  item,
  athleteId,
  athleteName,
  onClose,
}: {
  item: AthleteEventItem
  athleteId: string
  athleteName: string
  onClose: () => void
}) {
  const navigate = useNavigate()
  const { api } = useEnv()
  const marks = useParticipantMarks(item.event.id)
  const invalidate = useInvalidateEvent()
  const flight = useSingleFlight()

  return (
    <ParticipantSheet
      title={item.event.name}
      eventName={item.event.name}
      hasFee={!!item.event.fee}
      participant={{ athleteId, name: athleteName, status: item.status, payment: item.payment, result: item.result }}
      state={marks.states[athleteId]}
      keepOpen
      onSet={(patch) => void marks.set(athleteId, patch)}
      onRemove={async () => {
        try {
          const res = await flight.run(() => api.removeEventParticipant(item.event.id, athleteId))
          if (res === null) return false
          await invalidate(item.event.id)
          return true
        } catch {
          return false
        }
      }}
      link={{ label: 'Открыть событие', onClick: () => navigate(`/schedule/events/${item.event.id}`) }}
      onClose={onClose}
    />
  )
}
